// File: src/contexts/PermissionContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { RESOURCES, ACTIONS } from '../constants/permissions';
import { getApiBaseUrl } from '../utils/apiConfig';

const PermissionContext = createContext();

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children, user }) => {
  const [permissions, setPermissions] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const API_BASE_URL = getApiBaseUrl();

  /**
   * Fetch permissions from API
   * This is the single source of truth for permissions
   */
  const refreshPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user?.id) {
        setPermissions({});
        setRoles([]);
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedPermissions = data.permissions || {};
        const fetchedRoles = data.roles || [];

        setPermissions(fetchedPermissions);
        setRoles(fetchedRoles);
        setLastRefresh(new Date());

        // Update user object in localStorage with fresh permissions (for backward compatibility)
        try {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            const updatedUser = {
              ...parsedUser,
              permissions: fetchedPermissions,
              roles: fetchedRoles
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } catch (e) {
          console.error('Error updating user in localStorage:', e);
        }
      } else {
        console.error('Failed to fetch permissions:', response.status);
        // On error, try to use permissions from user object as fallback
        if (user?.permissions) {
          setPermissions(user.permissions);
        }
        if (user?.roles) {
          setRoles(user.roles);
        }
      }
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      // On error, try to use permissions from user object as fallback
      if (user?.permissions) {
        setPermissions(user.permissions);
      }
      if (user?.roles) {
        setRoles(user.roles);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, API_BASE_URL]);

  // Initial load and refresh when user changes
  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      refreshPermissions();
    } else {
      setPermissions({});
      setRoles([]);
      setLoading(false);
    }
  }, [user?.id, refreshPermissions]);

  // Auto-refresh permissions every 5 minutes
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const interval = setInterval(() => {
      refreshPermissions();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user?.id, refreshPermissions]);

  /**
   * Check if user has a specific permission
   * Uses constants for resource and action names
   * @param {string} resource - Resource name (use RESOURCES constant)
   * @param {string} action - Action name (use ACTIONS constant)
   * @returns {boolean} True if user has permission
   */
  const hasPermission = useCallback((resource, action) => {
    // Check for "all" permission (object format only)
    if (permissions.all === true || permissions.all === 'true') {
      return true;
    }

    // Check specific resource permission (object format only)
    const resourcePerms = permissions[resource];
    if (!resourcePerms || typeof resourcePerms !== 'object') {
      return false;
    }

    // Check specific action
    const actionValue = resourcePerms[action];
    if (actionValue === true || actionValue === 'true') {
      return true;
    }

    // Check if "manage" permission exists as fallback
    const manageValue = resourcePerms[ACTIONS.MANAGE];
    if (manageValue === true || manageValue === 'true') {
      return true;
    }

    return false;
  }, [permissions]);

  /**
   * Check if user has any of the specified permissions (OR logic)
   * @param {Array} permissionList - Array of {resource, action} objects
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }

    // Check for "all" permission
    if (permissions.all === true || permissions.all === 'true') {
      return true;
    }

    // Check each permission
    return permissionList.some(({ resource, action }) => hasPermission(resource, action));
  }, [permissions, hasPermission]);

  /**
   * Check if user has all of the specified permissions (AND logic)
   * @param {Array} permissionList - Array of {resource, action} objects
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = useCallback((permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }

    // Check for "all" permission
    if (permissions.all === true || permissions.all === 'true') {
      return true;
    }

    // Check all permissions
    return permissionList.every(({ resource, action }) => hasPermission(resource, action));
  }, [permissions, hasPermission]);

  /**
   * Check if user can access a specific organization
   * @param {string|number} organizationId - Organization ID
   * @returns {boolean} True if user can access the organization
   */
  const canAccess = useCallback((organizationId) => {
    // System Admin can access all organizations
    if (permissions.all === true || permissions.all === 'true') {
      return true;
    }

    // Check if user belongs to the organization
    if (user?.organization_id === organizationId || 
        String(user?.organization_id) === String(organizationId)) {
      return true;
    }

    // Check if user has organizations.read permission
    return hasPermission(RESOURCES.ORGANIZATIONS, ACTIONS.READ);
  }, [permissions, user?.organization_id, hasPermission]);

  /**
   * Get all user roles
   * @returns {Array} Array of role objects
   */
  const getRoles = useCallback(() => {
    return roles || [];
  }, [roles]);

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name
   * @returns {boolean} True if user has the role
   */
  const hasRole = useCallback((roleName) => {
    return roles.some(role => 
      role.name === roleName || 
      (typeof role === 'string' && role === roleName)
    );
  }, [roles]);

  /**
   * Check if user is System Admin
   * Uses only roles from API (no fallback to user.role field)
   * @returns {boolean} True if user is System Admin
   */
  const checkIsSystemAdmin = useCallback(() => {
    // Check permissions.all
    if (permissions.all === true || permissions.all === 'true') {
      return true;
    }
    
    // Check roles array (from API)
    return hasRole('System Admin');
  }, [permissions, hasRole]);

  const isSystemAdmin = checkIsSystemAdmin();

  const value = {
    permissions,
    roles,
    loading,
    lastRefresh,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    getRoles,
    hasRole,
    isSystemAdmin,
    refreshPermissions // Expose refresh function for manual refresh
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
